import { apiGet, apiPost, apiDelete, apiGetFileContent, apiUpload } from "./api.service";
import { getToken } from "./auth.service";
import { cryptoService } from './crypto.service';
import { MAX_FILE_SIZE, CHUNK_SIZE } from '../utils/consts';

export async function listMyFiles() {
    return apiGet('/users/files');
}

export async function listMyUploads() {
    return apiGet('/users/uploads?limit=20');
}

export async function listSharedFiles() {
    return apiGet('/users/files/shared');
}

export async function listSharesForFile(fileId) {
    return apiGet(`/users/files/${fileId}/shares`);
}

export async function getFileMetadata(fileId) {
    return apiGet(`/users/files/${fileId}`);
}

export async function getSharedFileMetadata(fileId) {
    return apiGet(`/users/files/shared/${fileId}`);
}

export async function shareFile(fileId, receiverDetails) {
    return apiPost(`/users/files/${fileId}/share`, receiverDetails);
}

export async function unshareFile(fileId, contactEmail) {
    return apiDelete(`/users/files/${fileId}/share/${contactEmail}`);
}

export async function downloadFile(fileId, progressCallback, signal) {
    try {
        const metadata = await getFileMetadata(fileId);
        if (metadata.isEncrypted) {
            // Add retry mechanism for private key availability
            let retryCount = 0;
            const maxRetries = 3;
            
            while (retryCount < maxRetries) {
                try {
                    const privateKey = cryptoService.privateKey;
                    if (!privateKey) {
                        retryCount++;
                        if (retryCount >= maxRetries) {
                            throw new Error('Private key not found. Please ensure encryption is initialized.');
                        }
                        // Wait a bit before retrying (exponential backoff)
                        await new Promise(resolve => setTimeout(resolve, 100 * retryCount));
                        continue;
                    }
                    
                    const encryptedFileKey = metadata.encryptionInfo.encryptedFileKey;
                    const fileKey = await cryptoService.decryptFileKey(encryptedFileKey, privateKey);
                    await multipartDownload(fileId, false, fileKey, progressCallback, signal);
                    return; // Success, exit the retry loop
                } catch (error) {
                    if (error.message.includes('Private key not found') && retryCount < maxRetries - 1) {
                        retryCount++;
                        // Wait a bit before retrying (exponential backoff)
                        await new Promise(resolve => setTimeout(resolve, 100 * retryCount));
                        continue;
                    }
                    throw error;
                }
            }
        }
    } catch (error) {
        console.error('[FilesService] Download error:', error);
        throw error;
    }
}

export async function downloadSharedFile(fileId, password, progressCallback, signal) {
    try {
        const metadataResponse = await getSharedFileMetadata(fileId);
        console.log('[FilesService] Shared file metadata response:', metadataResponse);
        
        const metadata = metadataResponse.file;
        if (metadata.isEncrypted) {
            // Add retry mechanism for private key availability
            let retryCount = 0;
            const maxRetries = 3;
            
            while (retryCount < maxRetries) {
                try {
                    const privateKey = cryptoService.privateKey;
                    if (!privateKey) {
                        retryCount++;
                        if (retryCount >= maxRetries) {
                            throw new Error('Private key not found. Please ensure encryption is initialized.');
                        }
                        // Wait a bit before retrying (exponential backoff)
                        await new Promise(resolve => setTimeout(resolve, 100 * retryCount));
                        continue;
                    }
                    
                    // Check for encryptedFileKey in different possible locations
                    let encryptedFileKey = metadataResponse.encryptedFileKey;
                    
                    // If not at root level, check in encryptionInfo
                    if (!encryptedFileKey && metadataResponse.encryptionInfo) {
                        encryptedFileKey = metadataResponse.encryptionInfo.encryptedFileKey;
                    }
                    
                    // If still not found, check in the file object
                    if (!encryptedFileKey && metadataResponse.file && metadataResponse.file.encryptionInfo) {
                        encryptedFileKey = metadataResponse.file.encryptionInfo.encryptedFileKey;
                    }
                    
                    console.log('[FilesService] Encrypted file key:', encryptedFileKey);
                    console.log('[FilesService] Encrypted file key type:', typeof encryptedFileKey);
                    console.log('[FilesService] Encrypted file key length:', encryptedFileKey?.length);
                    
                    if (!encryptedFileKey) {
                        throw new Error('Encrypted file key is missing from the shared file metadata');
                    }
                    
                    if (typeof encryptedFileKey !== 'string') {
                        throw new Error('Encrypted file key must be a string');
                    }
                    
                    // Validate base64 format (more permissive)
                    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
                    if (!base64Regex.test(encryptedFileKey)) {
                        console.warn('[FilesService] Encrypted file key appears to be malformed, but attempting to proceed:', encryptedFileKey);
                    }
                    
                    console.log('[FilesService] Attempting to decrypt file key with private key...');
                    console.log('[FilesService] Private key available:', !!privateKey);
                    
                    let fileKey;
                    try {
                        fileKey = await cryptoService.decryptFileKey(
                            encryptedFileKey,
                            privateKey
                        );
                        console.log('[FilesService] File key decryption successful');
                    } catch (decryptError) {
                        console.error('[FilesService] File key decryption failed:', decryptError);
                        throw decryptError;
                    }
                    await multipartDownload(fileId, true, fileKey, progressCallback, signal);
                    return; // Success, exit the retry loop
                } catch (error) {
                    if (error.message.includes('Private key not found') && retryCount < maxRetries - 1) {
                        retryCount++;
                        // Wait a bit before retrying (exponential backoff)
                        await new Promise(resolve => setTimeout(resolve, 100 * retryCount));
                        continue;
                    }
                    throw error;
                }
            }
        }
    } catch (error) {
        console.error('[FilesService] Download error:', error);
        throw error;
    }
}

export async function initiateMultipartUpload(initiateParams) {
    return  await apiPost('/users/uploads/multipart', initiateParams);
}

export async function uploadMultipartPart(uploadId, partNumber, objectKey, chunk, progressCallback, signal) {
    const partUrl = `/users/uploads/multipart/${encodeURIComponent(uploadId)}/part/${partNumber}?objectKey=${objectKey}`;
    const response = await apiUpload(partUrl, chunk, { 'Content-Type': 'application/octet-stream' }, (progress, progressInfo) => {
        if (progressCallback) {
            progressCallback(progressInfo); // Pass the progressInfo object directly
        }
    }, 'PUT', signal);
    if (!response.etag) {
        throw new Error(`No ETag received for part ${partNumber}`);
    }
    return {
        partNumber,
        etag: response.etag
    };
}

export async function completeMultipartUpload(completeParams) {
    return await apiPost(`/users/uploads/multipart/${encodeURIComponent(completeParams.uploadId)}/complete`, completeParams);
}

export async function multipartUpload({ file, encryptionInfo, onProgress, fileKey, signal }) {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const totalSize = file.size + (totalChunks * cryptoService.getIVSize()) + ( totalChunks * 16); // AUTH TAG SIZE
    const initiateParams = {
        name: file.name,
        contentType: file.type || 'application/octet-stream',
        size: totalSize,
        parts: totalChunks,
        isEncrypted: true,
        encryptionInfo
    };

    // Show immediate progress feedback to indicate work is starting
    if (onProgress) {
        onProgress(1, {
            fileName: file.name,
            uploaded: 0,
            total: file.size,
            status: 'Preparing file for upload...',
            currentChunk: 0,
            totalChunks,
            bytesUploaded: 0,
            bytesTotal: file.size,
            partProgress: 0
        });
    }

    const { uploadId, objectKey } = await initiateMultipartUpload(initiateParams);
    const parts = [];
    let completedBytes = 0; // Track bytes from completed parts
    let lastProgressUpdate = 1; // Start from 1% since we already showed initial progress

    for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
        // Check for abort signal before processing each part
        if (signal && signal.aborted) {
            throw new Error('Upload was aborted');
        }
        
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        let fileChunk = file.slice(start, end);
        const originalChunkSize = fileChunk.size;
        
        // Show encryption progress for this chunk
        if (onProgress) {
            onProgress(Math.max(1, (completedBytes / file.size) * 100), {
                fileName: file.name,
                uploaded: completedBytes,
                total: file.size,
                status: `Encrypting chunk ${partNumber} of ${totalChunks}...`,
                currentChunk: partNumber,
                totalChunks,
                bytesUploaded: completedBytes,
                bytesTotal: file.size,
                partProgress: 0
            });
        }
        
        let encryptedChunk = await cryptoService.encryptFileChunk(fileChunk, fileKey);

        fileChunk = null; // Clear the original chunk from memory
        
        // Check for abort signal before uploading the part
        if (signal && signal.aborted) {
            throw new Error('Upload was aborted');
        }
        
        const part = await uploadMultipartPart(uploadId, partNumber, objectKey, encryptedChunk, (partProgress) => {
            const currentPartProgress = (partProgress.loaded / partProgress.total) * originalChunkSize;
            const totalProgress = completedBytes + currentPartProgress;
            const progress = (totalProgress / file.size) * 100;

            // Update progress every 0.5% change
            if (Math.abs(progress - lastProgressUpdate) >= 0.5) {
                lastProgressUpdate = progress;
                if (onProgress) {
                    onProgress(progress, {
                        fileName: file.name,
                        uploaded: totalProgress,
                        total: file.size,
                        status: 'Encrypting and uploading file',
                        currentChunk: partNumber,
                        totalChunks,
                        bytesUploaded: totalProgress,
                        bytesTotal: file.size,
                        partProgress: (partProgress.loaded / partProgress.total) * 100
                    });
                } else {
                    console.warn('[FilesService] onProgress callback is undefined');
                }
            }

        }, signal);

        // Add the part to the parts array
        parts.push(part);
        
        completedBytes += originalChunkSize; // Track original file bytes, not encrypted
        encryptedChunk = null;  // Clear the encrypted chunk from memory

        // Explicitly update progress after each part completes
        if (onProgress) {
            const progress = (completedBytes / file.size) * 100;
            onProgress(progress, {
                fileName: file.name,
                uploaded: completedBytes,
                total: file.size,
                status: 'Encrypting and uploading file',
                currentChunk: partNumber,
                totalChunks,
                bytesUploaded: completedBytes,
                bytesTotal: file.size,
                partProgress: 100
            });
        }

        // Ensure we show 100% when part is complete
        if (onProgress && partNumber === totalChunks) {
            onProgress(100, {
                fileName: file.name,
                uploaded: file.size,
                total: file.size,
                status: 'Upload complete',
                currentChunk: totalChunks,
                totalChunks,
                bytesUploaded: file.size,
                bytesTotal: file.size,
                partProgress: 100
            });
        }
    }
    
    // Check for abort signal before completing the upload
    if (signal && signal.aborted) {
        throw new Error('Upload was aborted');
    }
    
    return await completeMultipartUpload({
        uploadId,
        objectKey,
        parts
    });
}

export async function multipartDownload(fileId, isShared = false, fileKey, onProgress, signal) {
    const startTime = performance.now();
    console.log('[FilesService] Starting multipart download for file:', fileId);
    console.log('[FilesService] Abort signal provided:', !!signal);
    console.log('[FilesService] Abort signal aborted:', signal?.aborted);
    
    // Check for abort signal before starting
    if (signal && signal.aborted) {
        console.log('[FilesService] Download aborted before starting');
        throw new Error('Download was aborted');
    }
    
    const metadata = isShared ? await getSharedFileMetadata(fileId) : await getFileMetadata(fileId);

    const metadataTime = performance.now();
    console.log('[FilesService] Metadata fetch took:', metadataTime - startTime, 'ms');
    console.log('[FilesService] Abort signal aborted after metadata:', signal?.aborted);

    // Check for abort signal after metadata fetch
    if (signal && signal.aborted) {
        console.log('[FilesService] Download aborted after metadata fetch');
        throw new Error('Download was aborted');
    }

    const fileMetadata = isShared ? metadata.file : metadata;
    if (!fileMetadata.isEncrypted) {
        throw new Error('File is not encrypted');
    }

    // Show immediate progress feedback to indicate work is starting
    if (onProgress) {
        onProgress(1, {
            fileName: fileMetadata.originalFilename || fileMetadata.name,
            downloaded: 0,
            total: fileMetadata.size,
            status: 'Preparing file for download...'
        });
    }

    const progressTime = performance.now();
    console.log('[FilesService] Progress callback took:', progressTime - metadataTime, 'ms');

    // Collect all decrypted chunks for traditional download
    const decryptedChunks = [];

    const token = await getToken();
    const tokenTime = performance.now();
    console.log('[FilesService] Token fetch took:', tokenTime - progressTime, 'ms');
    console.log('[FilesService] Abort signal aborted after token:', signal?.aborted);
    
    // Check for abort signal after token fetch
    if (signal && signal.aborted) {
        console.log('[FilesService] Download aborted after token fetch');
        throw new Error('Download was aborted');
    }
    
    const endpoint = isShared ?
        `/users/files/shared/${fileId}/download` :
        `/users/files/${fileId}/download`;

    console.log('[FilesService] Making API request to:', endpoint);
    console.log('[FilesService] Abort signal aborted before API request:', signal?.aborted);
    const response = await apiGetFileContent(endpoint, {
        Accept: 'application/octet-stream',
        Authorization: `Bearer ${token}`
    }, signal);

    const responseTime = performance.now();
    console.log('[FilesService] API response took:', responseTime - tokenTime, 'ms');
    console.log('[FilesService] Abort signal aborted after API response:', signal?.aborted);

    // Check for abort signal after getting the response
    if (signal && signal.aborted) {
        console.log('[FilesService] Download aborted after API response');
        throw new Error('Download was aborted');
    }

    if (!response.ok) {
        const errorText = await response.text();
        console.error('[FilesService] Download failed:', {
            status: response.status,
            statusText: response.statusText,
            error: errorText
        });
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    const originalFileSize = fileMetadata.size;
    const totalChunks = Math.ceil(originalFileSize / CHUNK_SIZE);
    const ivSize = cryptoService.getIVSize() + 16; // AUTH TAG SIZE
    
    // Optimized calculation: most chunks have the same size
    const standardChunkSize = CHUNK_SIZE;
    const standardEncryptedSize = ivSize + standardChunkSize;
    const lastChunkOriginalSize = originalFileSize % CHUNK_SIZE || standardChunkSize;
    const lastChunkEncryptedSize = ivSize + lastChunkOriginalSize;
    
    const encryptedChunkSizes = new Array(totalChunks);
    encryptedChunkSizes.fill(standardEncryptedSize);
    if (totalChunks > 0) {
        encryptedChunkSizes[totalChunks - 1] = lastChunkEncryptedSize;
    }

    const calculationTime = performance.now();
    console.log('[FilesService] Chunk size calculation took:', calculationTime - responseTime, 'ms');
    console.log('[FilesService] File size:', originalFileSize, 'bytes, Total chunks:', totalChunks);
  
    const reader = response.body.getReader();
    let completedBytes = 0;
    let lastProgressUpdate = 1; // Start from 1% since we already showed initial progress
    let currentChunkIndex = 0;
    let chunkBuffer = new Uint8Array(0);

    console.log('[FilesService] Starting chunk processing...');
    console.log('[FilesService] Abort signal aborted before chunk processing:', signal?.aborted);
    const processingStartTime = performance.now();

    try {
        while (currentChunkIndex < totalChunks) {
            // Check for abort signal before processing each chunk
            if (signal && signal.aborted) {
                console.log('[FilesService] Download aborted during chunk processing at chunk:', currentChunkIndex);
                throw new Error('Download was aborted');
            }
            
            const { done, value } = await reader.read();
            if (done && chunkBuffer.length === 0) {
                break;
            }

            if (value) {
                const newBuffer = new Uint8Array(chunkBuffer.length + value.length);
                newBuffer.set(chunkBuffer);
                newBuffer.set(value, chunkBuffer.length);
                chunkBuffer = newBuffer;
            }

            const requiredSize = encryptedChunkSizes[currentChunkIndex];
            const isLastChunk = currentChunkIndex === totalChunks - 1;
            

            // Process chunk if we have enough data
            if (chunkBuffer.length >= requiredSize) {
                // Check for abort signal before processing the chunk
                if (signal && signal.aborted) {
                    console.log('[FilesService] Download aborted before processing chunk:', currentChunkIndex);
                    throw new Error('Download was aborted');
                }
                
                const chunkStartTime = performance.now();
                const chunkData = chunkBuffer.slice(0, requiredSize);
                let fileChunk = new Blob([chunkData]);
                
                // Show decryption progress for this chunk
                if (onProgress) {
                    onProgress(Math.max(1, (completedBytes / originalFileSize) * 100), {
                        fileName: fileMetadata.originalFilename || fileMetadata.name,
                        downloaded: completedBytes,
                        total: originalFileSize,
                        status: `Decrypting chunk ${currentChunkIndex + 1} of ${totalChunks}...`
                    });
                }
                
                let decryptedChunk = await cryptoService.decryptFileChunk(fileChunk, fileKey);
                
                decryptedChunks.push(decryptedChunk);

                fileChunk = null;
                decryptedChunk = null;

                const originalChunkSize = requiredSize - ivSize;
                completedBytes += originalChunkSize;

                if (onProgress) {
                    const progress = (completedBytes / originalFileSize) * 100;
                    if (Math.abs(progress - lastProgressUpdate) >= 0.5) {
                        lastProgressUpdate = progress;
                        onProgress(progress, {
                            fileName: fileMetadata.originalFilename || fileMetadata.name,
                            downloaded: completedBytes,
                            total: originalFileSize,
                            status: 'Downloading and decrypting file'
                        });
                    }
                }
                
                chunkBuffer = chunkBuffer.slice(requiredSize);
                currentChunkIndex++;
                
                // Log every 10th chunk or every chunk for small files
                if (currentChunkIndex % 10 === 0 || totalChunks <= 10) {
                    const chunkTime = performance.now();
                    console.log(`[FilesService] Chunk ${currentChunkIndex}/${totalChunks} processed in:`, chunkTime - chunkStartTime, 'ms');
                    console.log(`[FilesService] Abort signal aborted at chunk ${currentChunkIndex}:`, signal?.aborted);
                }
                
                if(currentChunkIndex >= totalChunks) break;
            }
            
            // If stream is done and we still have the last chunk to process
            if (done && isLastChunk && chunkBuffer.length > 0) {
                // Check for abort signal before processing the last chunk
                if (signal && signal.aborted) {
                    console.log('[FilesService] Download aborted before processing last chunk');
                    throw new Error('Download was aborted');
                }
                
                const actualSize = Math.min(requiredSize, chunkBuffer.length);
                const chunkData = chunkBuffer.slice(0, actualSize);
                let fileChunk = new Blob([chunkData]);
                let decryptedChunk = await cryptoService.decryptFileChunk(fileChunk, fileKey);
                
                decryptedChunks.push(decryptedChunk);

                fileChunk = null;
                decryptedChunk = null;

                const originalChunkSize = actualSize - ivSize;
                completedBytes += originalChunkSize;
                
                chunkBuffer = chunkBuffer.slice(actualSize);
                currentChunkIndex++;
                break;
            }
            
            if (done) {
                break;
            }
        }
        
        if (chunkBuffer.length > 0) {
            console.warn(`[FilesService] ${chunkBuffer.length} bytes remaining in buffer after finishing.`);
        }

        // Check for abort signal before combining chunks
        if (signal && signal.aborted) {
            console.log('[FilesService] Download aborted before combining chunks');
            throw new Error('Download was aborted');
        }

        // Combine all decrypted chunks into a single blob
        console.log('[FilesService] Combining decrypted chunks...');
        const combinedBlob = new Blob(decryptedChunks, { type: fileMetadata.contentType || 'application/octet-stream' });
        
        // Check for abort signal before creating download link
        if (signal && signal.aborted) {
            console.log('[FilesService] Download aborted before creating download link');
            throw new Error('Download was aborted');
        }
        
        // Create download link and trigger download
        const downloadUrl = URL.createObjectURL(combinedBlob);
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = fileMetadata.originalFilename || fileMetadata.name || 'downloaded-file';
        downloadLink.style.display = 'none';
        
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        
        // Clean up the object URL
        URL.revokeObjectURL(downloadUrl);

        const totalProcessingTime = performance.now();
        console.log('[FilesService] Total processing time:', totalProcessingTime - processingStartTime, 'ms');
        console.log('[FilesService] Total download time:', totalProcessingTime - startTime, 'ms');

        if (onProgress) {
            onProgress(100, {
                fileName: fileMetadata.originalFilename || fileMetadata.name,
                downloaded: originalFileSize,
                total: originalFileSize,
                status: 'Download complete'
            });
        }

        return {
            filename: fileMetadata.originalFilename || fileMetadata.name,
            contentType: fileMetadata.contentType,
            size: originalFileSize
        };

    } catch (error) {
        console.error('[FilesService] Multipart download failed during processing:', error);
        throw error;
    }
}

export async function directUpload({ file, encryptedFile, encryptionInfo, onProgress, signal }) {
    const formData = new FormData();
    formData.append('file', encryptedFile, file.name);
    formData.append('name', file.name);
    formData.append('contentType', file.type);
    formData.append('size', encryptedFile.size.toString());
    formData.append('isEncrypted', 'true');
    formData.append('encryptionInfo', JSON.stringify(encryptionInfo));

    return await apiUpload('/users/uploads/direct', formData, { 'Content-Type': 'multipart/form-data' }, onProgress, 'POST', signal);
}

export async function uploadFile({ file, fileKey, encryptionInfo, onProgress, signal }) {
    try {
        if (file.size > MAX_FILE_SIZE) {
            // Encrypt the file in chunks
            return await multipartUpload({ file, encryptionInfo, onProgress, fileKey, signal });
        } else {
            // Show immediate progress feedback for small files
            if (onProgress) {
                onProgress(1, {
                    fileName: file.name,
                    uploaded: 0,
                    total: file.size,
                    status: 'Preparing file for upload...',
                    currentChunk: 1,
                    totalChunks: 1,
                    bytesUploaded: 0,
                    bytesTotal: file.size,
                    partProgress: 0
                });
            }
            
            // Encrypt the file in one chunk
            const encryptedFile = await cryptoService.encryptFileChunk(file, fileKey);
            return await directUpload({ file, encryptedFile, encryptionInfo, onProgress, signal });
        }
    } catch (error) {
        console.error('[FilesService] Upload error:', error);
        return {
            error: error.message,
            code: error.code
        };
    }
}

export async function abortUpload(uploadId) {
    return apiDelete(`/users/uploads/multipart/${uploadId}`);
}

export async function abortDownload() {
    // For downloads, we need to abort any ongoing fetch requests
    // This is typically handled by AbortController in the calling code
    // This function serves as a placeholder for any server-side cleanup if needed
    console.log('[FilesService] Download abort requested');
    return { success: true, message: 'Download abort requested' };
}

export async function deleteFile(fileId) {
    return apiDelete(`/users/files/${fileId}`);
}

// TODO: move to files.service
export async function uploadFileDirectly(formData) {
    return await apiUpload('/users/uploads/direct', formData, { 'Content-Type': 'multipart/form-data' }, undefined, 'POST');
}